import { cn } from '@/lib/utils';
import * as icons from 'lucide-react-native/icons';
import type { LucideProps } from 'lucide-react-native';
import { useColor } from '~/global/hooks/useColor';
import { useContext, useMemo } from 'react';
import { TextClassContext } from '@/components/ui/text';
import { Color, colors } from '@/lib/colors';

export interface IconProps extends LucideProps {
  name: keyof typeof icons;
}

export const Icon = ({
  name,
  className,
  color: colorProp,
  ...props
}: IconProps) => {
  const LucideIcon = icons[name];
  if (!LucideIcon) {
    throw new Error(`Icon ${name} not found`);
  }
  const inheritClassName = useContext(TextClassContext);
  const cln = cn('text-foreground', className);
  const colorName = useMemo(() => {
    const className = cn(inheritClassName, cln);
    const classes = className.split(' ');
    const colorClass = classes
      .filter((c) => c.startsWith('text-'))
      .map((c) => {
        const [, ...colorParts] = c.split('-');
        const color = colorParts.join('-');
        if (color in colors.dark) return color;
        return null;
      })
      .filter(Boolean)[0];
    return (colorClass as Color) ?? 'foreground';
  }, [inheritClassName, cln]);
  const computedColor = useColor(colorName);
  const color = colorProp ?? computedColor;
  return (
    <LucideIcon {...props} className={cln} color={color} pointerEvents="none" />
  );
};
